import { useParams } from 'react-router-dom';
import type { StatCardModel } from '@/types/stats';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { DataTable } from '@/components/composites/DataTable';
import { Button } from '@/components/primitives/Button';
import { useClientDetail } from '@/hooks/useClientDetail';
import type { ClientDailyRow } from '@/data/clientDetail';
import { formatDayShort, formatDayWithName } from '@/lib/dates';
import styles from './ClientDetailPage.module.css';

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { client, dailyRows } = useClientDetail(clientId);
  const clientName = client?.name ?? 'Unknown Client';

  const rows = dailyRows;
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  const totalBillable = rows.reduce((s, r) => s + r.billable, 0);
  const daysActive = rows.length;
  const activeProjects = client?.projects.length ?? 0;

  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total Hours', value: `${totalHours.toFixed(1)}h` },
    { label: 'Billable Hours', value: `${totalBillable.toFixed(1)}h`, sublabel: '100%' },
    { label: 'Days Active', value: daysActive },
    { label: 'Active Projects', value: activeProjects },
  ];

  return (
    <>
      <PageHeader
        title={clientName}
        actions={
          <Button variant="secondary" onClick={() => {}}>
            Export CSV
          </Button>
        }
      />
      <StatCardGrid cards={cards} />
      <h2 className={styles.sectionTitle}>Daily Summary</h2>
      <DataTable<ClientDailyRow>
        caption="Client daily summary"
        columns={[
          {
            key: 'date',
            header: 'Date',
            width: '12%',
            render: (r) => <span className={styles.dateCell}>{formatDayShort(r.date)}</span>,
          },
          {
            key: 'day',
            header: 'Day',
            width: '8%',
            render: (r) => {
              const parts = formatDayWithName(r.date).split(', ');
              return <span className={styles.dayCell}>{parts[1]}</span>;
            },
          },
          {
            key: 'hours',
            header: 'Hours',
            align: 'right',
            width: '12%',
            render: (r) => <span className={styles.mono}>{r.hours.toFixed(1)}h</span>,
          },
          {
            key: 'billable',
            header: 'Billable',
            align: 'right',
            width: '12%',
            render: (r) => <span className={styles.mono}>{r.billable.toFixed(1)}h</span>,
          },
          {
            key: 'general',
            header: 'General',
            align: 'right',
            width: '12%',
            render: (r) => (
              <span className={`${styles.mono} ${r.general === 0 ? styles.zero : ''}`}>
                {r.general.toFixed(1)}h
              </span>
            ),
          },
          {
            key: 'firstActivity',
            header: 'First Activity',
            align: 'right',
            width: '16%',
            render: (r) => <span className={styles.mono}>{r.firstActivity}</span>,
          },
          {
            key: 'lastActivity',
            header: 'Last Activity',
            align: 'right',
            width: '16%',
            render: (r) => <span className={styles.mono}>{r.lastActivity}</span>,
          },
        ]}
        rows={[...rows]}
        getRowKey={(r) => r.date}
        footer={
          <tr>
            <td>Total</td>
            <td />
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {totalHours.toFixed(1)}h
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {totalBillable.toFixed(1)}h
            </td>
            <td className={styles.muted} style={{ textAlign: 'right' }}>
              0.0h
            </td>
            <td className={styles.muted} style={{ textAlign: 'right' }}>
              &mdash;
            </td>
            <td className={styles.muted} style={{ textAlign: 'right' }}>
              &mdash;
            </td>
          </tr>
        }
      />
    </>
  );
}
